/**
 * Frontend Application Insights wrapper.
 *
 * Why this module exists
 * ----------------------
 * design.md §Monitoring requires the SPA to report unhandled errors and
 * SignalR connection failures so operators can correlate them with the
 * Functions-side metrics emitted by `api/src/infra/telemetry.ts`. The
 * `@microsoft/applicationinsights-web` SDK is the canonical browser
 * instrumentation; this module wraps it with three guarantees:
 *
 *   1. **No-op when env unset.** The connection string is read from
 *      `import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING` at the call site
 *      (`main.tsx`) and forwarded here. When undefined/empty the wrapper
 *      returns a no-op client so local development never tries to ingest.
 *   2. **Errors never propagate.** A misbehaving telemetry SDK must not
 *      crash an error boundary or interrupt a user action. Each public
 *      method wraps its body in try/catch.
 *   3. **Idempotent init.** Repeated calls with the same connection string
 *      reuse the cached SDK instance so React StrictMode's double-mount and
 *      hot-reload don't spin up multiple instrumentation streams.
 *
 * The exported surface is intentionally narrow (`trackException`,
 * `trackEvent`) — view/route auto-tracking is left to the SDK's default
 * page-view collection so we don't have to thread the client through
 * `main.tsx`'s React tree.
 *
 * Requirements covered: R4.5 (operational visibility for reconnects),
 * R8.3 (rate-limit blocked count on the API side has a frontend mirror via
 * `trackException` when chatApi returns 429).
 */

import { ApplicationInsights } from '@microsoft/applicationinsights-web';

// ---------------------------------------------------------------------------
// Public interface
// ---------------------------------------------------------------------------

/**
 * Narrow telemetry surface consumed by the SPA. Mirrors the backend
 * `TelemetryClient` interface (minus `trackMetric`, which is server-side
 * only) so reasoning about the system is symmetric.
 */
export interface FrontendTelemetry {
  /**
   * Emits an exception. Use on every catch block where an error is
   * meaningful to operators (e.g. SignalR connect failure, sendMessage
   * 5xx response, unhandled `componentDidCatch`).
   */
  trackException(error: Error, properties?: Record<string, string>): void;

  /**
   * Emits a custom event. Use for discrete occurrences without a numeric
   * measurement (e.g. "signalr.reconnect.attempt", "rateLimit.blocked").
   */
  trackEvent(name: string, properties?: Record<string, string>): void;
}

// ---------------------------------------------------------------------------
// Internal state — cached SDK instance keyed by connection string
// ---------------------------------------------------------------------------

/**
 * Cached SDK instance. We key on the connection string so a hot-reload that
 * swaps env vars (uncommon but possible) builds a fresh instance, while
 * normal repeated calls with the same string reuse the existing one.
 */
let cachedInstance: ApplicationInsights | undefined;
let cachedKey: string | undefined;

/**
 * No-op fallback used when the connection string is unset. Defined at
 * module scope so every disabled caller shares one allocation.
 */
const noopClient: FrontendTelemetry = {
  trackException: () => {
    /* intentional no-op */
  },
  trackEvent: () => {
    /* intentional no-op */
  },
};

// ---------------------------------------------------------------------------
// Public init
// ---------------------------------------------------------------------------

/**
 * Initialises the frontend telemetry client.
 *
 * - When `connectionString` is `undefined`, empty, or whitespace-only,
 *   returns the no-op client. This is the default in local development.
 * - Otherwise builds (or reuses) an `ApplicationInsights` instance and
 *   returns a wrapper whose `trackException` / `trackEvent` forward to it.
 *
 * Call this once at SPA bootstrap (`main.tsx`) and pass the returned
 * client to whatever code path needs to report.
 */
export function initTelemetry(
  connectionString: string | undefined,
): FrontendTelemetry {
  const trimmed =
    connectionString === undefined ? '' : connectionString.trim();
  if (trimmed.length === 0) {
    return noopClient;
  }

  const sdk = ensureSdkInstance(trimmed);
  if (sdk === undefined) {
    // SDK init failed (e.g. exception in loadAppInsights). Fall back to the
    // no-op client so the app keeps running — telemetry must never block UX.
    return noopClient;
  }

  return {
    trackException: (error, properties) => {
      try {
        sdk.trackException({ exception: error, properties });
      } catch (err) {
        // Critical: do not let a telemetry failure mask the original error.
        // eslint-disable-next-line no-console
        console.error('[telemetry] trackException failed', err);
      }
    },
    trackEvent: (name, properties) => {
      try {
        sdk.trackEvent({ name, properties });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[telemetry] trackEvent failed', err);
      }
    },
  };
}

/**
 * Returns the cached SDK instance for the given connection string,
 * building (and starting) a new one if necessary.
 *
 * Returns `undefined` if SDK construction throws — the caller falls back
 * to the no-op client so the app keeps running.
 */
function ensureSdkInstance(
  connectionString: string,
): ApplicationInsights | undefined {
  if (cachedInstance !== undefined && cachedKey === connectionString) {
    return cachedInstance;
  }

  try {
    const instance = new ApplicationInsights({
      config: {
        connectionString,
        // Auto-collect page views and unhandled exceptions out of the box;
        // disable correlation headers because Static Web Apps domain != the
        // SignalR Service domain and we don't want unnecessary CORS noise.
        enableAutoRouteTracking: true,
        disableAjaxTracking: false,
        disableFetchTracking: false,
        enableCorsCorrelation: false,
        disableExceptionTracking: false,
      },
    });
    instance.loadAppInsights();
    cachedInstance = instance;
    cachedKey = connectionString;
    return instance;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[telemetry] SDK init failed; falling back to no-op', err);
    return undefined;
  }
}
